import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgProgress } from '@ngx-progressbar/core';
import { SelectComponent } from 'ng2-select';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import { ListeableComponent } from '../../../../shared/components/interfaces/listeable.component';
import { NgSelectOptionModel } from '../../../../shared/model/ng-select-option.model';
import { ExportToExcelService } from '../../../../shared/services/export-to-excel.service';
import { UIDialogService } from '../../../../shared/services/ui-dialog.service';
import { SortUtils } from '../../../../shared/utils/sort.utils';
import { AuthService } from '../../../security/service/auth.service';
import { FilterModel } from '../../model/filter.model';
import { ProductSearchModel } from '../../model/product-search.model';
import { ProductUIMessages } from '../../model/product-ui.messages';
import { ProductRoutingStates } from '../../product-routing.module';
import { KeywordService } from '../../service/keyword.service';
import { ProductService } from '../../service/product.service';
import { ProductDeactivateComponent } from '../deactivate/product-deactivate.component';
import { ProductListResultsComponent } from '../product-list-results/product-list-results.component';

@Component({
  selector: 'product-list',
  templateUrl:
    './console/app-js/modules/product/components/list/product-list.component.html'
})
export class ProductListComponent extends APIOperableComponent
  implements OnInit, ListeableComponent {
  @ViewChild('requiresAudit', { static: true })
  public requiresAuditSelect: SelectComponent;
  @ViewChild('productTypeComponent', { static: true })
  public ngProductTypeSelect: SelectComponent;
  @ViewChild('partAndProductListResultsComponent', { static: true })
  public dataTableComponent: ProductListResultsComponent;
  protected auditOptions = [
    { id: 'true', text: 'Yes' },
    { id: 'false', text: 'No' }
  ];
  private datePipe: DatePipe;
  private searchModel = new ProductSearchModel();
  private filterModel = new FilterModel();
  public allLineOfServices: Array<any> = [];
  public allTypes: Array<any> = [];
  public allProductLines: Array<any> = [];
  public yesNoAnswers: Array<any>;
  protected searching = false;
  public noResults = false;
  protected deleting = false;
  protected fullscreen = false;
  protected makes: Array<NgSelectOptionModel>;
  protected makesData: Array<NgSelectOptionModel>;
  protected makeFilterSelected: any = '';
  private EXCEL_FILE_NAME = 'PartAndProductSearchResult.xlsx';
  protected EXPORT_RESULTS_LIMIT = 2000;
  protected selectedRows = [];
  protected modelStringFilter: string;
  protected originalData: Array<any>;
  public selectedAuditType: any;
  public allAuditTypes: Array<any> = [];

  /**
   *  Constructor
   * @param  {NgProgressService} progressService
   * @param  {ToastrService} toastr
   * @param {ExportToExcelService} exportToExcelService
   * @param {UIDialogService} dialogService
   * @param {ProductService} productService
   */
  constructor(
    progressService: NgProgress,
    toastr: ToastrService,
    authService: AuthService,
    private productService: ProductService,
    private router: Router,
    private exportToExcelService: ExportToExcelService,
    private dialogService: UIDialogService,
    private keywordService: KeywordService
  ) {
    // super call to APIOperableComponent
    super(progressService, toastr, authService);
    this.modelStringFilter = '';
    this.datePipe = new DatePipe('en-US');
  }

  /**
   * Initiates the Notice Module.
   */
  ngOnInit(): void {
    // Retain search model value from service
    this.searchModel = this.productService.getProductSearchInput();
    this.filterModel = this.searchModel.filterModel;

    this.modelStringFilter = this.filterModel.model;

    this.loadMakesList();
    this.getAuditOptions();
    if (this.searchModel.results.length > 0) {
      this.onSearch();
    }
  }

  /**
   * Get the list of makes
   */
  protected loadMakesList() {
    this.productService.getProductMakesForNgSelect().subscribe(
      (result: Array<NgSelectOptionModel>) => {
        this.makes = result;
        this.makesData = this.makes.slice(0);
        // auto selected value for makes
        if (this.searchModel.filterModel != undefined) {
          const selectedResult = this.makes.filter(
            s => s.text === this.searchModel.filterModel.make
          );
          this.makeFilterSelected =
            selectedResult[0] == undefined
              ? { text: this.searchModel.filterModel.make, id: null }
              : selectedResult[0];
        }
      },
      error => this.onAPIError(ProductUIMessages.LOAD_MAKES_ERROR)
    );
  }

  protected valueNormalizer = (text: Observable<string>) =>
    text.pipe(
      map((description: string) => {
        return {
          text: description
        };
      })
    );

  /**
   * Filters Makes combox items by input value.
   * @param value
   */
  protected handleMakesComboBoxFilter(value) {
    this.makesData = this.makes.filter(
      s => s.text.toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
  }

  /**
   * Selects a make in the kendo combo box.
   * @param make
   */
  protected onSelectedMake(make: NgSelectOptionModel): void {
    if (make !== undefined && make.text) {
      this.filterModel.make = make.text;
    } else {
      this.filterModel.make = null;
      this.makeFilterSelected = null;
      this.handleMakesComboBoxFilter('');
    }
  }

  /**
   * Performs a new Search at Page 1
   */
  onSearch(): void {
    this.searchModel.results = [];
    this.searchModel.pageNumber = 1;
    // Creating a copy of the original model.
    let filterModelCopy = Object.assign({}, this.filterModel);
    // Ensure that Product Id is trimmed so the request wont throw an error.
    filterModelCopy.id = filterModelCopy.id
      ? encodeURIComponent(filterModelCopy.id.trim())
      : '';
    this.productService.setProductSearchInput(this.searchModel);
    this.searchModel.filterModel = filterModelCopy;
    this.searchProduct(true);
    this.finishProgress();
  }

  /**
   * Delete all selected rows
   */
  onDeleteItems(): void {
    this.deleting = true;
    this.dialogService
      .open(ProductDeactivateComponent, [
        { provide: 'products', useValue: this.selectedRows }
      ])
      .then(result => {
        if (result.associateKeywords) {
          this.associateKeywordsAndDeactivateProduct(result.productsSelected);
        } else {
          this.deactivateProduct();
        }
      })
      .catch(result => {
        this.deleting = false;
      });
  }

  /**
   * 1. API calls to associate deactivated make/model keywords.
   * 2. API calls to deactivate Product.
   */
  private associateKeywordsAndDeactivateProduct(
    activeProducts: Array<any>
  ): void {
    this.keywordService
      .createKeywordAssociationForProductDeactivation(
        this.selectedRows,
        activeProducts.map(product => product.id)
      )
      .subscribe(results => {
        this.checkForFailedKeywordAssociations(results, activeProducts);
        this.deactivateProduct();
      });
  }

  /**
   * Check if some associations were not successful and inform the user.
   * @param results: Array
   * @param products: Array
   */
  private checkForFailedKeywordAssociations(
    results: Array<any>,
    products: Array<any>
  ): void {
    let failedAssociations: Set<any> = new Set();
    if (results && results.length > 0) {
      results.forEach(result => {
        if (result && result.status === 'failed') {
          failedAssociations.add(result.productId);
        }
      });
      if (failedAssociations.size > 0) {
        const message = this.keywordService.buildKeywordAssociationErrorMessage(
          Array.from(failedAssociations),
          products
        );
        this.toastr.error(message, 'API Error.', { timeOut: 10000 });
      }
    }
  }
  /**
   * API call to deactivate Product.
   */
  private deactivateProduct(): void {
    this.productService.deleteListOfProducts(this.selectedRows).subscribe(
      () => {
        this.deleting = false;
        this.searchProduct();
        this.onAPISuccess(ProductUIMessages.DEACTIVATE_PRODUCTS_SUCCESS);
      },
      () => this.onAPIError(ProductUIMessages.DEACTIVATE_PRODUCTS_ERROR)
    );
  }

  /**
   * Redirect user to Add Product view.
   */
  onAddItem(): void {
    this.router.navigate([`/console/${ProductRoutingStates.LIST.url}/add`]);
  }

  /**
   * On Rest button click, clear out all search filters to initial state.
   */
  onReset(): void {
    this.noResults = false;
    this.modelStringFilter = '';
    this.selectedAuditType = '';
    this.makeFilterSelected = null;
    this.filterModel = Object.assign(new FilterModel());
    this.searchModel.results = [];
    this.productService.setProductSearchInput(new ProductSearchModel());
  }

  /**
   * Change the Data Table into Fullscreen Mode
   */
  onFullscreenMode(): void {
    this.fullscreen = !this.fullscreen;
  }

  /**
   * Performs a Search with all parameters selected
   */
  private searchProduct(refreshGrid = false): void {
    this.progressRef.start(); // START PROGRESS
    this.searching = true;
    if (refreshGrid) {
      this.originalData = [];
      this.searchModel.pageNumber = 1;
    } else {
      this.searchModel.pageNumber += 1;
    }
    this.productService.searchPartAndProduct(this.searchModel).subscribe(
      (res: any) => {
        if (res.responseCode === 200 && res.results) {
          this.noResults = res.results.length === 0 ? true : false;
          this.searchModel = this.productService.getSearchResultModel(
            this.searchModel,
            res
          );
          this.searchModel.results = res.results;
          let totalData = JSON.parse(JSON.stringify(this.searchModel.results));
          this.originalData.push(...totalData);
        } else {
          this.noResults = true;
        }
        this.searchModel.results = [...this.originalData];
        this.progressRef.complete();
        this.productService.setProductSearchInput(this.searchModel);
        this.searching = false;
      },
      () => {
        this.onAPIError(ProductUIMessages.SEARCH_PRODUCTS_ERROR);
        this.searchModel = new ProductSearchModel();
        this.searchModel.filterModel = this.filterModel;
        this.productService.setProductSearchInput(this.searchModel);
        this.searching = false;
      }
    );
  }

  /**
   * Populate All Line of Service and bind the result to the ng-select component
   */
  private getAllLineOfService(): void {
    this.productService.getAllLineOfService().subscribe(
      (res: any) => {
        let lineOfServices = res.results.map(los => {
          return {
            id: los.id,
            text: los.name
          };
        });

        this.allLineOfServices = lineOfServices.sort((a, b) =>
          SortUtils.compareByProperty(a, b, 'text')
        );
      },
      () => this.onAPIError(ProductUIMessages.LOAD_LINES_OF_SERVICE_ERROR)
    );
  }

  /**
   * Populate All Product Lines and bind the result to the ng-select component
   */
  private getAllProductLines(): void {
    this.productService.getAllProductLines().subscribe(
      (res: any) => {
        let productLines = res.results.map(productLine => {
          return {
            id: productLine.id,
            text: productLine.name
          };
        });

        this.allProductLines = productLines.sort((a, b) =>
          SortUtils.compareByProperty(a, b, 'text')
        );
      },
      () => this.onAPIError(ProductUIMessages.LOAD_PRODUCT_LINES_ERROR)
    );
  }

  /**
   * Populate All Product Type and bind the result to the ng-select component
   */

  protected getAuditOptions(): void {
    this.allAuditTypes = this.auditOptions;
    const selectedAuditTypes = this.allAuditTypes.filter(
      s => s.text.indexOf(this.filterModel.requiresAudit) !== -1
    );
    this.selectedAuditType = selectedAuditTypes[0];
  }

  /**
   * Attach the LOS selected to the Model
   * @param LOSName
   */
  protected selectedLOS(LOSName: any): void {
    if (LOSName) {
      this.filterModel.lineOfService = LOSName.text;
    }
  }

  /**
   * Attach the Type selected to the Model.
   * @param type
   */
  protected selectedType(type: any): void {
    if (type) {
      this.filterModel.type = type.text;
    }
  }

  /**
   * Attach the Type selected to the Model.
   * @param type
   */

  protected onSelectedAudit(type: any): void {
    if (type) {
      this.filterModel.requiresAudit = type.text === 'Yes' ? 'Yes' : 'No';
    } else {
      this.filterModel.requiresAudit = null;
    }
  }

  /**
   * Removes Type value from the Model
   */
  protected removeAudit(type: any): void {
    this.filterModel.requiresAudit = null;
  }

  /**
   * On Product Line item selected from dropdown assign this new value.
   * @param productLine
   */
  private onProductLineSelect(productLine: any): void {
    if (productLine) {
      this.filterModel.productLine = productLine.text;
    }
  }

  /**
   * Remove the value from the Model
   * @param value
   */
  protected onRemovedLOS(): void {
    this.filterModel.lineOfService = '';
  }

  /**
   * Remove the value from the Model
   */
  protected onRemovedProductLine(): void {
    this.filterModel.productLine = '';
  }

  /**
   * Removes Type value from the Model
   */
  protected removeType(): void {
    this.filterModel.type = null;
  }

  /**
   * Removes Expense value from the Model
   */
  protected removeExpense(): void {
    this.filterModel.isExpense = undefined;
  }

  /**
   * Removes Truck Stock value from the Model
   */
  protected removeTruckStock(): void {
    this.filterModel.truckStock = undefined;
  }

  /**
   * Executes export to excel spreadsheet current results.
   */
  protected exportResults(): void {
    this.progressRef.start(); // START PROGRESS

    if (this.searchModel.totalCount > this.EXPORT_RESULTS_LIMIT) {
      this.onAPIError(
        ProductUIMessages.VALIDATION_EXPORT_PRODUCTS_LIMIT_EXCEEDED.replace(
          '{0}',
          this.EXPORT_RESULTS_LIMIT.toString()
        )
      );
      return;
    }

    this.productService
      .searchPartAndProductWithMaxRows(
        this.searchModel,
        this.searchModel.totalCount
      )
      .subscribe(
        (res: any) => {
          let exportResults = res.results;
          let jsondata = exportResults.map(row => {
            return {
              'Product Id': row.id,
              'Part Number': row.partNumber,
              Product: row.name,
              Description: row.description,
              Created: this.datePipe.transform(row.createdDate),
              Updated: this.datePipe.transform(row.updatedDate),
              Active: row.isActive
            };
          });
          this.exportToExcelService.exportFromJsonToExcel(
            jsondata,
            this.EXCEL_FILE_NAME,
            'xlsx'
          );
          this.onAPISuccess(ProductUIMessages.EXPORT_PRODUCTS_SUCCESS);
        },
        () => this.onAPIError(ProductUIMessages.EXPORT_PRODUCTS_ERROR)
      );
  }

  /**
   * Pass the number of selected rows
   * @param event
   */
  protected deleteEvent(selected: any): void {
    this.selectedRows = selected;
  }

  /**
   * Filters current results by the input text Model.
   * Filtering occurs per page only, is 100% done on the client side.
   * @param $event
   */
  protected onFilterByModel(): void {
    let value = this.modelStringFilter.toLocaleLowerCase();
    // setting searchModel properties on keyup
    this.searchModel.filterModel.model = value === '' ? '' : value;
    this.productService.setProductSearchInput(this.searchModel);
    // filter our data
    const temp = this.originalData.filter(row => {
      return row.model.toLowerCase().indexOf(value) !== -1 || !value;
    });
    // update the rows
    this.searchModel.results = temp;
  }

  /**
   * Infinity scrolling pagination functionality
   */
  infinityScroll(): void {
    if (this.searchModel.pageNumber < this.searchModel.totalPages) {
      this.searchProduct();
    }
  }
}
